/*
  Neroli — header.js

  Web components so the theme editor can re-inject section HTML without
  leaving dead listeners behind:
    <site-header>  — sticky behavior, click-to-open dropdowns
    <mobile-menu>  — drawer, focus trap, Escape, overlay dismiss
*/

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* ------------------------------------------------------------------
   <site-header>
   ------------------------------------------------------------------ */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.stickyMode = this.dataset.sticky || 'never';
    this.dropdownTrigger = this.dataset.dropdownTrigger || 'hover';
    this.spacer = document.querySelector('[data-header-spacer]');

    this.lastScrollY = window.scrollY;
    this.isStuck = false;

    if (this.stickyMode !== 'never') {
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.measure();
    }

    this.publishStickyOffset = this.publishStickyOffset.bind(this);
    this.publishStickyOffset();
    window.addEventListener('resize', this.publishStickyOffset, { passive: true });

    if (this.dropdownTrigger === 'click') {
      this.onDropdownClick = this.onDropdownClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.querySelectorAll('.site-header__nav-item--has-children > a').forEach((link) => {
        link.addEventListener('click', this.onDropdownClick);
      });
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('resize', this.publishStickyOffset);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  /* Publishes --neroli-sticky-offset: how much of the top of the viewport is
     covered by fixed chrome, so sticky page elements (the collection filter
     sidebar and toolbar) can offset themselves instead of sliding underneath.

     A stuck header is position:fixed at z-index 60; a sticky announcement bar
     is position:fixed at z-index 40 — so the header covers the announcement
     bar rather than stacking below it. The offset is therefore the larger of
     the two, not their sum.

     Published once as a constant rather than recalculated on stick/unstick,
     because a sticky element whose top value changes mid-scroll visibly jumps. */
  publishStickyOffset() {
    const announcement = document.querySelector('.announcement-bar--sticky');
    const announcementHeight = announcement ? announcement.offsetHeight : 0;
    const headerHeight =
      this.stickyMode === 'never' ? 0 : this.naturalHeight || this.offsetHeight;

    document.documentElement.style.setProperty(
      '--neroli-sticky-offset',
      `${Math.max(headerHeight, announcementHeight)}px`
    );
  }

  /* ---- Sticky ---- */

  measure() {
    // Capture the natural height before we ever go fixed, so the spacer
    // reserves the right amount of room and the page doesn't jump.
    this.naturalHeight = this.offsetHeight;
    this.naturalTop = this.getBoundingClientRect().top + window.scrollY;

    // Expose the height globally so other sections (e.g. the collection
    // banner) can reserve matching space when this header is transparent
    // and therefore removed from normal document flow.
    if (this.dataset.transparent === 'true') {
      document.documentElement.style.setProperty(
        '--transparent-header-offset',
        `${this.naturalHeight}px`
      );
    }
  }

  onResize() {
    if (this.isStuck) return;
    this.measure();
  }

  onScroll() {
    const y = window.scrollY;
    const shouldStick = y > this.naturalTop + this.naturalHeight;
    const wasStuck = this.isStuck;

    if (shouldStick && !this.isStuck) {
      this.stick();
    } else if (!shouldStick && this.isStuck) {
      this.unstick();
    }

    // Skip the hide/show check on the tick where the header first becomes
    // sticky. Without this, sticking is almost always triggered by a
    // downward scroll, so the header would hide in the same frame it
    // appears — it's never actually visible to the person scrolling.
    if (this.isStuck && wasStuck && this.stickyMode === 'scroll-up') {
      const scrollingDown = y > this.lastScrollY;
      this.classList.toggle('site-header--hidden', scrollingDown);
    }

    this.lastScrollY = y;
  }

  stick() {
    this.isStuck = true;
    this.classList.add('site-header--stuck');
    if (this.spacer) {
      this.spacer.style.height = `${this.naturalHeight}px`;
      this.spacer.hidden = false;
    }
  }

  unstick() {
    this.isStuck = false;
    this.classList.remove('site-header--stuck', 'site-header--hidden');
    if (this.spacer) {
      this.spacer.hidden = true;
      this.spacer.style.height = '';
    }
  }

  /* ---- Click dropdowns ---- */

  onDropdownClick(event) {
    const item = event.currentTarget.closest('.site-header__nav-item');
    if (!item) return;

    // Desktop only — the mobile drawer handles its own <details> disclosure.
    if (window.matchMedia('(max-width: 989px)').matches) return;

    const wasOpen = item.classList.contains('is-open');
    event.preventDefault();
    this.closeAllDropdowns();

    if (!wasOpen) {
      item.classList.add('is-open');
      event.currentTarget.setAttribute('aria-expanded', 'true');
    }
  }

  onDocumentClick(event) {
    if (this.contains(event.target)) return;
    this.closeAllDropdowns();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeAllDropdowns();
  }

  closeAllDropdowns() {
    this.querySelectorAll('.site-header__nav-item.is-open').forEach((item) => {
      item.classList.remove('is-open');
      const link = item.querySelector('a[aria-expanded]');
      if (link) link.setAttribute('aria-expanded', 'false');
    });
  }
}

customElements.define('site-header', SiteHeader);

/* ------------------------------------------------------------------
   <mobile-menu>
   ------------------------------------------------------------------ */
class MobileMenu extends HTMLElement {
  connectedCallback() {
    // Two possible triggers can exist at once: the mobile-only hamburger
    // and the desktop hamburger shown when "Enable hamburger menu" is on.
    // Both must open this same drawer.
    this.toggles = Array.from(document.querySelectorAll('[data-menu-toggle]'));
    this.panel = this.querySelector('.mobile-menu__panel');
    if (this.toggles.length === 0 || !this.panel) return;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onToggleClick = this.onToggleClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.toggles.forEach((toggle) => toggle.addEventListener('click', this.onToggleClick));
    this.querySelectorAll('[data-mobile-menu-close]').forEach((el) => {
      el.addEventListener('click', this.close);
    });
  }

  disconnectedCallback() {
    this.toggles?.forEach((toggle) => toggle.removeEventListener('click', this.onToggleClick));
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  }

  get isOpen() {
    return !this.hidden;
  }

  onToggleClick() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.hidden = false;
    this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';

    document.addEventListener('keydown', this.onKeydown);

    /* Focus the dialog itself, not its first link.
       focusable[0] is the logo, so opening the drawer drew a focus ring
       around it — which looks like a stray box around the logo on a phone,
       where nobody expects a focus indicator from a tap.

       Moving focus to the panel is the standard dialog pattern: a screen
       reader announces the dialog's own label, the focus trap still works
       because focus is inside the panel, and the first Tab goes to the logo
       as it should. The panel takes no visible ring, because a container is
       not something the user navigated to. */
    this.panel.focus();
  }

  close() {
    this.hidden = true;
    this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = '';

    document.removeEventListener('keydown', this.onKeydown);
    this.toggles[0]?.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    // Recompute each Tab — <details> disclosure changes what's focusable.
    const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('mobile-menu', MobileMenu);
